import React, { useMemo } from 'react'
import PendientesPorCiaDonut from './pendientes-por-cia-donut'
import AyudaExportar from './ayuda-exportar'

/**
 * Los dos donuts de "Situación actual" lado a lado — **V1.1 (RG-541, 04-09-2026)**.
 * Izquierda: Pendientes de Gestión · Derecha: Aprobados Pendientes de Emisión.
 *
 * 🔑 **La leyenda es UNA sola, compartida, y el color de cada compañía es el mismo en los dos
 *    donuts.** Es lo que hace comparable la pareja: sin un `colorMap` común, `recharts` asigna el
 *    color por POSICIÓN dentro de cada dataset, así que una compañía que en un donut es la 1.ª y
 *    en el otro la 3.ª saldría de dos colores distintos y la lectura lado a lado engañaría.
 *
 * El orden del mapa lo fija la suma de casos de ambos datasets: la compañía con más presencia se
 * lleva el primer color de la paleta y queda estable aunque cambie el filtro.
 */

const PALETA = [
    '#4F517A', '#007bff', '#28a745', '#dc3545', '#ffc107',
    '#fd7e14', '#6f42c1', '#17a2b8', '#e83e8c', '#20c997',
    '#6610f2', '#343a40'
]

// `onExportarGestion` / `onExportarEmision` (RG-602): reciben la porción clicada de cada donut.
const DonutsPendientesCia = ({ dataGestion = [], dataEmision = [], onExportarGestion, onExportarEmision }) => {
    const { colorMap, companias } = useMemo(() => {
        const acumulado = new Map()

        for (const fila of [...dataGestion, ...dataEmision]) {
            const clave = fila.companiaId || fila.compania
            const previo = acumulado.get(clave)
            if (previo) {
                previo.cantidad += fila.cantidad
            } else {
                acumulado.set(clave, { clave, compania: fila.compania, cantidad: fila.cantidad })
            }
        }

        const ordenadas = Array.from(acumulado.values()).sort((a, b) => b.cantidad - a.cantidad)
        const mapa = {}
        ordenadas.forEach((c, i) => { mapa[c.clave] = PALETA[i % PALETA.length] })

        return { colorMap: mapa, companias: ordenadas }
    }, [dataGestion, dataEmision])

    const hayDatos = dataGestion.length || dataEmision.length

    return (
        <div>
            <div className='row g-2'>
                <div className='col-6'>
                    <div className='text-center small fw-semibold text-muted mb-1'>Pendientes de Gestión</div>
                    <PendientesPorCiaDonut
                        data={dataGestion}
                        colorMap={colorMap}
                        showLegend={false}
                        height={220}
                        onExportar={onExportarGestion}
                    />
                </div>
                <div className='col-6'>
                    <div className='text-center small fw-semibold text-muted mb-1'>Aprobados Pendientes de Emisión</div>
                    <PendientesPorCiaDonut
                        data={dataEmision}
                        colorMap={colorMap}
                        showLegend={false}
                        height={220}
                        onExportar={onExportarEmision}
                    />
                </div>
            </div>
            {(onExportarGestion || onExportarEmision) && hayDatos ? (
                <div className='mt-2'>
                    <AyudaExportar texto='Clic en una porción para descargar sus casos en Excel' />
                </div>
            ) : null}

            {hayDatos ? (
                <div
                    className='d-flex flex-wrap justify-content-center gap-3 pt-2 mt-2'
                    style={{ borderTop: '1px solid #e9ecef' }}
                >
                    {companias.map((c) => (
                        <div key={c.clave} className='d-flex align-items-center gap-1'>
                            <span
                                style={{
                                    width: 10, height: 10, borderRadius: '50%',
                                    background: colorMap[c.clave], display: 'inline-block'
                                }}
                            />
                            <span style={{ fontSize: 12 }}>{c.compania}</span>
                        </div>
                    ))}
                </div>
            ) : null}
        </div>
    )
}

export default DonutsPendientesCia
